/**
 * Video export size / quality presets. Sizes reuse the Photo Studio reel
 * platforms so a reel and a timeline export for the same platform match.
 */

import type { VideoExportSettings, VideoFps } from '@canvas/shared-types';
import { REEL_PLATFORMS } from '@/data/reelPlatforms';

export interface VideoSizePreset {
  id: string;
  label: string;
  width: number;
  height: number;
}

export const ARTBOARD_PRESET_ID = 'artboard';

/** Standard YouTube video: landscape 1080p. Shorts stay vertical in the reel list. */
export const YOUTUBE_VIDEO_PRESET: VideoSizePreset = {
  id: 'youtube',
  label: 'YouTube · 1920×1080',
  width: 1920,
  height: 1080,
};

export const VIDEO_SIZE_PRESETS: VideoSizePreset[] = [
  ...REEL_PLATFORMS.flatMap((p) => {
    const preset = {
      id: p.id,
      label: `${p.label} · ${p.width}×${p.height}`,
      width: p.width,
      height: p.height,
    };
    return p.id === 'shorts' ? [preset, YOUTUBE_VIDEO_PRESET] : [preset];
  }),
  { id: 'landscape', label: 'Landscape 1080p · 1920×1080', width: 1920, height: 1080 },
  { id: 'square', label: 'Square · 1080×1080', width: 1080, height: 1080 },
];

export const VIDEO_FPS_OPTIONS: VideoFps[] = [24, 25, 30, 60];

export type VideoQuality = VideoExportSettings['quality'];

/** Bits per pixel per frame. ~0.1 is a good H.264 default for motion graphics. */
const BITS_PER_PIXEL: Record<VideoQuality, number> = {
  draft: 0.05,
  standard: 0.1,
  high: 0.16,
};

export const VIDEO_QUALITY_OPTIONS: Array<{ id: VideoQuality; label: string }> = [
  { id: 'draft', label: 'Draft (smaller file)' },
  { id: 'standard', label: 'Standard' },
  { id: 'high', label: 'High (larger file)' },
];

export function bitrateFor(width: number, height: number, fps: number, quality: VideoQuality): number {
  return Math.round(width * height * fps * BITS_PER_PIXEL[quality]);
}

/** Keyframe every 2 s: seekable without bloating the file. */
export function keyFrameIntervalFor(fps: number): number {
  return Math.max(1, Math.round(fps * 2));
}

/** Recompute the derived fields after the user changes size, fps or quality. */
export function withDerivedExportFields(settings: VideoExportSettings): VideoExportSettings {
  return {
    ...settings,
    videoBitrate: bitrateFor(settings.width, settings.height, settings.fps, settings.quality),
    keyFrameInterval: keyFrameIntervalFor(settings.fps),
  };
}

export function applySizePreset(
  settings: VideoExportSettings,
  presetId: string,
  artboard: { width: number; height: number }
): VideoExportSettings {
  const preset = VIDEO_SIZE_PRESETS.find((p) => p.id === presetId);
  if (preset) {
    return withDerivedExportFields({
      ...settings,
      platformId: preset.id,
      width: preset.width,
      height: preset.height,
    });
  }
  // Match artboard: long edge capped at 1920, even dimensions.
  const scale = Math.min(1, 1920 / Math.max(artboard.width, artboard.height, 1));
  const even = (x: number) => Math.max(2, Math.round((x * scale) / 2) * 2);
  return withDerivedExportFields({
    ...settings,
    platformId: 'custom',
    width: even(artboard.width),
    height: even(artboard.height),
  });
}

export function sizePresetIdFor(settings: VideoExportSettings): string {
  const match = VIDEO_SIZE_PRESETS.find(
    (p) => p.id === settings.platformId && p.width === settings.width && p.height === settings.height
  );
  return match ? match.id : ARTBOARD_PRESET_ID;
}
